import {
  ActivityOptions,
  ActivityOrderBy,
  DateTypeFilter,
  getBaseColumnsForWorkspace,
  GlobalAdminColumns,
  MetaPillState,
  Severity,
  toggleMetaItem,
  toggleMetaPill,
  useCurrentAccount,
  useGroups,
  useRoles,
  useStatefulWorkspaceRoutines,
  useWorkspaceLabels,
  WorkspaceType,
} from '@gripp/shared-logic';
import {
  Control,
  Controller,
  UseFormSetValue,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Pressable, Text, View } from 'react-native';
import { Colors } from '../../themes/colors';
import { severityToVariant } from '../crop-circle/Components/Type/severityMapping';
import { TypeIcon } from '../crop-circle/Components/Type/TypeIcon';
import { Icon } from '../crop-circle/icons';
import { colors as CropCircleColors, tokens } from '../crop-circle/tokens';
import { DateRangeSelector } from '../forms/datePicker/dateRangeSelector';
import { FilterChip, filterChipStyleValues } from './filterChip';
import type { FilterSectionType } from './filterSectionTypes';
import { themedStyleSheet } from '../../themes/themedStyleSheet';

export type FilterVariant = 'activity' | 'asset' | 'account';

export type ColumnsConfig = {
  baseColumns: string[];
  adminColumns?: string[];
  i18nPrefix: string;
};

export type FilterContentProps = {
  variant: FilterVariant;
  sections: readonly FilterSectionType[];
  control: Control<any>;
  setValue?: UseFormSetValue<any>;
  selectedDateType?: string;
  workspaceId?: string;
  isGlobalAdmin: boolean;
  isOEM?: boolean;
  columnsConfig?: ColumnsConfig;
  /**
   * View-only search query that narrows which filter chips are shown. Each chip
   * renders only when its label matches; a section is hidden when none of its
   * chips match. Empty/undefined leaves every section rendered as-is. This never
   * touches the form values or the server filter — it only affects visibility.
   */
  searchQuery?: string;
};

export function FilterContent({
  variant,
  sections,
  control,
  setValue,
  selectedDateType = '',
  workspaceId,
  isGlobalAdmin,
  isOEM = false,
  columnsConfig,
  searchQuery,
}: FilterContentProps) {
  const { t } = useTranslation();
  const { groups = [] } = useGroups(isGlobalAdmin ? undefined : workspaceId);
  const { labels = [] } = useWorkspaceLabels(
    isGlobalAdmin ? undefined : workspaceId
  );
  const workspaceRoutines = useStatefulWorkspaceRoutines(workspaceId);
  const { roles: accountRoles = [] } = useRoles();
  const { account } = useCurrentAccount();
  const isRestricted =
    account?.effectivePermissions?.includes('*.restricted') ?? false;

  const has = (key: FilterSectionType) => sections.includes(key);

  // The combined-groups/labels toggles only do anything when specific items
  // are selected (they switch $in → $all across them), so only surface them then.
  const groupsFieldName = variant === 'activity' ? 'groupIds' : 'groups';
  const watchedGroups = useWatch({ control, name: groupsFieldName }) as
    | (string | null | undefined)[]
    | undefined;
  const hasSpecificGroups = (watchedGroups ?? []).filter(Boolean).length > 0;
  const watchedLabels = useWatch({ control, name: 'labelIds' }) as
    | (string | null | undefined)[]
    | undefined;
  const hasSpecificLabels = (watchedLabels ?? []).filter(Boolean).length > 0;

  // ---- Activity-only options ----
  const activityOptions = [
    {
      value: ActivityOptions.EVERYTHING,
      label: t('activity.filter.all'),
      icon: null,
    },
    {
      value: ActivityOptions.MENTIONED,
      label: t('activity.filter.mentions'),
      icon: 'At',
    },
    {
      value: ActivityOptions.SCHEDULED,
      label: t('activity.filter.scheduled'),
      icon: 'Scheduled',
    },
    {
      value: ActivityOptions.NOT_SCHEDULED,
      label: t('activity.filter.notScheduled'),
      icon: 'Not Scheduled',
    },
    {
      value: ActivityOptions.PINNED,
      label: t('activity.filter.pinned'),
      icon: 'Pin',
    },
  ];
  const dateOptions = [
    { value: DateTypeFilter.ALL, label: t('activity.filter.all') },
    { value: DateTypeFilter.TODAY, label: t('activity.filter.today') },
    { value: DateTypeFilter.THISWEEK, label: t('activity.filter.thisWeek') },
    { value: DateTypeFilter.THISMONTH, label: t('activity.filter.thisMonth') },
    {
      value: DateTypeFilter.CUSTOMDATE,
      label: t('activity.filter.customDate'),
    },
  ];
  const sortOptions = [
    {
      value: ActivityOrderBy.MostRecentUpdated,
      label: t('activity.sort.mostRecent'),
    },
    {
      value: ActivityOrderBy.ReminderDate,
      label: t('activity.sort.reminderDate'),
    },
    { value: ActivityOrderBy.CreationDate, label: t('activity.sort.oldest') },
  ];
  const activityTypesOrder = [
    Severity.Red,
    Severity.Yellow,
    Severity.Green,
    Severity.Purple,
    Severity.Blue,
  ];
  const getSeverityLabel = (severity: string) => {
    const map: Record<string, string> = {
      [Severity.Red]: t('activity.filter.urgent'),
      [Severity.Yellow]: t('activity.filter.pending'),
      [Severity.Green]: t('activity.filter.solved'),
      [Severity.Purple]: t('activity.filter.notes'),
      [Severity.Blue]: t('activity.filter.routines'),
    };
    return map[severity] ?? severity;
  };
  const onPressSeverityFilter = (
    severity: string | null,
    value: (string | undefined)[] | null | undefined,
    onChange: (...event: any[]) => void
  ) => {
    if (severity === null) {
      onChange([]);
      return;
    }
    let newValue: (string | undefined)[];
    if (!value || value.length === 0) newValue = [severity];
    else if (!value.includes(severity)) newValue = value.concat(severity);
    else newValue = value.filter((v) => v !== severity);
    onChange(newValue);
  };
  const isAllSeveritySelected = (
    value: (string | undefined)[] | null | undefined
  ) => !value || value.length === 0;

  // ---- Asset-only: base columns ----
  const baseColumns = getBaseColumnsForWorkspace(
    isOEM ? WorkspaceType.Oem : undefined
  );

  // ---- Column label resolution (shared by visibility + render) ----
  const getAssetColumnLabel = (column: string) =>
    column.startsWith('routine:')
      ? workspaceRoutines?.find((r) => r.id === column.replace('routine:', ''))
          ?.name ?? column
      : t(`assets.filter.columns.${column}`);

  const allAssetColumns = Array.from(
    new Set([
      ...baseColumns.filter((col) => col !== 'name'),
      ...(isGlobalAdmin ? GlobalAdminColumns : []),
      ...(workspaceRoutines?.map((r) => `routine:${r.id}`) || []),
    ])
  );

  const getAccountColumnLabel = (column: string) =>
    columnsConfig ? t(`${columnsConfig.i18nPrefix}.columns.${column}`) : column;

  const allAccountColumns = columnsConfig
    ? Array.from(
        new Set([
          ...columnsConfig.baseColumns.filter((col) => col !== 'name'),
          ...(isGlobalAdmin ? columnsConfig.adminColumns ?? [] : []),
        ])
      )
    : [];

  // ---- Search: view-only filtering of chips/sections ----
  const query = (searchQuery ?? '').trim().toLowerCase();
  const matches = (label: string) =>
    query === '' || label.toLowerCase().includes(query);

  const showActivityTypes =
    variant === 'activity' &&
    has('activityTypes') &&
    (matches(t('activity.filter.all')) ||
      activityTypesOrder.some((s) => matches(getSeverityLabel(s))));
  const showOptions =
    variant === 'activity' &&
    has('options') &&
    activityOptions.some((o) => matches(o.label));
  // Account keeps the legacy "Default" pill; activity/asset use the All /
  // Not-Grouped meta pills. Match the section against whichever labels it shows.
  const groupsMetaLabels =
    variant === 'account'
      ? [t('account.filter.default')]
      : [
          t('activity.filter.all'),
          variant === 'activity'
            ? t('activity.filter.notGrouped')
            : t('assets.filter.notGrouped'),
        ];
  const showGroups =
    has('groups') &&
    (groupsMetaLabels.some((l) => matches(l)) ||
      groups.some((g: { name: string }) => matches(g.name)));
  const showAccountColumns =
    variant === 'account' &&
    has('columns') &&
    !!columnsConfig &&
    (matches(t(`${columnsConfig.i18nPrefix}.columns.name`)) ||
      allAccountColumns.some((c) => matches(getAccountColumnLabel(c))));
  const showAccountTypes =
    variant === 'account' &&
    has('accountTypes') &&
    (matches(t('account.filter.default')) ||
      accountRoles.some((r) => matches(r.name)));
  const showLabels =
    variant === 'activity' &&
    has('labels') &&
    (matches(t('activity.filter.all')) ||
      matches(t('activity.filter.notLabeled')) ||
      labels.some((l: { name: string }) => matches(l.name)));
  const showDate =
    variant === 'activity' &&
    has('date') &&
    !!setValue &&
    dateOptions.some((o) => matches(o.label));
  const showSort =
    variant === 'activity' &&
    has('sort') &&
    sortOptions.some((o) => matches(o.label));
  const showAssetColumns =
    variant === 'asset' &&
    has('columns') &&
    (matches(t('assets.filter.columns.name')) ||
      allAssetColumns.some((c) => matches(getAssetColumnLabel(c))));

  const anyVisible =
    showActivityTypes ||
    showOptions ||
    showGroups ||
    showAccountColumns ||
    showAccountTypes ||
    showLabels ||
    showDate ||
    showSort ||
    showAssetColumns;

  return (
    <>
      {/* ACTIVITY TYPES (activity only) */}
      {showActivityTypes && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('activity.filter.activityTypes')}
          </Text>
          <Controller
            control={control}
            name="severity"
            render={({ field: { onChange, value } }) => {
              const allSelected = isAllSeveritySelected(value);
              return (
                <View style={sharedStyles.optionsContainer}>
                  {matches(t('activity.filter.all')) && (
                    <Pressable
                      onPress={() =>
                        onPressSeverityFilter(null, value, onChange)
                      }
                      style={[
                        sharedStyles.optionChip,
                        allSelected && sharedStyles.optionChipSelected,
                      ]}
                    >
                      <Text
                        style={[
                          sharedStyles.optionChipText,
                          allSelected && sharedStyles.optionChipTextSelected,
                        ]}
                      >
                        {t('activity.filter.all')}
                      </Text>
                    </Pressable>
                  )}
                  {activityTypesOrder
                    .filter((severity) => matches(getSeverityLabel(severity)))
                    .map((severity) => {
                      const isSelected = value?.includes(severity);
                      const variantStyle = severityToVariant[severity];
                      return (
                        <Pressable
                          key={severity}
                          onPress={() =>
                            onPressSeverityFilter(severity, value, onChange)
                          }
                          style={[
                            sharedStyles.optionChip,
                            isSelected && sharedStyles.optionChipSelected,
                          ]}
                        >
                          <TypeIcon variant={variantStyle} size={16} />
                          <Text
                            style={[
                              sharedStyles.optionChipText,
                              isSelected && sharedStyles.optionChipTextSelected,
                            ]}
                          >
                            {getSeverityLabel(severity)}
                          </Text>
                        </Pressable>
                      );
                    })}
                </View>
              );
            }}
          />
        </View>
      )}

      {/* OPTIONS (activity only) */}
      {showOptions && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('activity.filter.options')}
          </Text>
          <Controller
            control={control}
            name="activityOption"
            render={({ field: { onChange, value } }) => (
              <View style={sharedStyles.optionsContainer}>
                {activityOptions
                  .filter((option) => matches(option.label))
                  .map((option) => {
                    const isSelected = value === option.value;
                    return (
                      <Pressable
                        key={option.value}
                        onPress={() => onChange(option.value)}
                        style={[
                          sharedStyles.optionChip,
                          isSelected && sharedStyles.optionChipSelected,
                        ]}
                      >
                        {option.icon && (
                          <Icon
                            name={option.icon as any}
                            size={16}
                            color={
                              isSelected
                                ? Colors.role.textOnInverse
                                : Colors.role.textStrong
                            }
                          />
                        )}
                        <Text
                          style={[
                            sharedStyles.optionChipText,
                            isSelected && sharedStyles.optionChipTextSelected,
                          ]}
                        >
                          {option.label}
                        </Text>
                      </Pressable>
                    );
                  })}
              </View>
            )}
          />
        </View>
      )}

      {/* GROUPS (activity: groupIds; asset/account: groups) */}
      {showGroups && (
        <View style={sharedStyles.section}>
          <View style={sharedStyles.sectionHeader}>
            <Text style={[sharedStyles.sectionTitle, { marginBottom: 0 }]}>
              {variant === 'activity'
                ? t('activity.filter.groups')
                : variant === 'account'
                ? t('account.filter.groups')
                : t('assets.filter.groups')}
            </Text>
            {/* Combined-groups toggle is activity/asset-only; AccountFilterModel
                does not carry isCombinedGroupsFilter. Only shown once specific
                groups are selected — it's a no-op otherwise. */}
            {variant !== 'account' && hasSpecificGroups && (
              <CombinedFilterToggle
                control={control}
                name="isCombinedGroupsFilter"
                accessibilityLabel={
                  variant === 'activity'
                    ? t('activity.filter.combinedGroups')
                    : t('assets.filter.combinedGroups')
                }
                testID="groups-combined-filter-toggle"
              />
            )}
          </View>
          <GroupsFilterPills
            control={control}
            setValue={setValue}
            fieldName={variant === 'activity' ? 'groupIds' : 'groups'}
            groups={groups}
            notGroupedLabel={
              variant === 'activity'
                ? t('activity.filter.notGrouped')
                : t('assets.filter.notGrouped')
            }
            allLabel={t('activity.filter.all')}
            defaultLabel={t('account.filter.default')}
            // Account has no backend support for the All/Not-Grouped meta pills,
            // so it keeps the simple Default + groups selector.
            metaPills={variant !== 'account'}
            showUngrouped={!isRestricted}
            searchQuery={searchQuery}
          />
        </View>
      )}

      {/* COLUMNS (account) — rendered between GROUPS and ACCOUNT TYPES so the
          account panel reads Groups → Columns → Account Types, matching the
          Assets panel layout. Other variants render their COLUMNS section at
          the end of the panel. */}
      {showAccountColumns && columnsConfig && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t(`${columnsConfig.i18nPrefix}.columnSelection`)}
          </Text>
          <Controller
            control={control}
            name="columns"
            render={({ field: { onChange, value } }) => {
              const currentValue = (value || []).filter(
                (c): c is string => c != null
              );
              const selectableBaseColumns = columnsConfig.baseColumns.filter(
                (col) => col !== 'name'
              );
              const allColumns = Array.from(
                new Set([
                  ...selectableBaseColumns,
                  ...(isGlobalAdmin ? columnsConfig.adminColumns ?? [] : []),
                ])
              );
              const onPressColumn = (column: string) => {
                if (column === 'name') return;
                let newValue: string[];
                if (currentValue.includes(column))
                  newValue = currentValue.filter((c) => c !== column);
                else newValue = [...currentValue, column];
                if (!newValue.includes('name'))
                  newValue = ['name', ...newValue];
                onChange(newValue);
              };
              const effectiveValue = currentValue.includes('name')
                ? currentValue
                : ['name', ...currentValue];
              return (
                <View style={sharedStyles.optionsContainer}>
                  {matches(t(`${columnsConfig.i18nPrefix}.columns.name`)) && (
                    <Pressable
                      key="name"
                      disabled={true}
                      style={[
                        sharedStyles.optionChip,
                        sharedStyles.optionChipSelected,
                        sharedStyles.optionChipDisabled,
                      ]}
                    >
                      <Text
                        style={[
                          sharedStyles.optionChipText,
                          sharedStyles.optionChipTextSelected,
                        ]}
                      >
                        {t(`${columnsConfig.i18nPrefix}.columns.name`)}
                      </Text>
                    </Pressable>
                  )}
                  {allColumns
                    .filter((column) => matches(getAccountColumnLabel(column)))
                    .map((column) => {
                      const isSelected = effectiveValue.includes(column);
                      return (
                        <Pressable
                          key={column}
                          onPress={() => onPressColumn(column)}
                          style={[
                            sharedStyles.optionChip,
                            isSelected && sharedStyles.optionChipSelected,
                          ]}
                        >
                          <Text
                            style={[
                              sharedStyles.optionChipText,
                              isSelected && sharedStyles.optionChipTextSelected,
                            ]}
                          >
                            {t(`${columnsConfig.i18nPrefix}.columns.${column}`)}
                          </Text>
                        </Pressable>
                      );
                    })}
                </View>
              );
            }}
          />
        </View>
      )}

      {/* ACCOUNT TYPES (account only) */}
      {showAccountTypes && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('account.filter.accountTypes')}
          </Text>
          <Controller
            control={control}
            name="roles"
            render={({ field: { onChange, value } }) => {
              const currentValue = (value || []).filter(
                (v): v is string => v != null && v !== ''
              );
              const isDefaultSelected = currentValue.length === 0;
              const onPressRole = (roleId: string) => {
                let newValue: string[];
                if (currentValue.length === 0) newValue = [roleId];
                else if (!currentValue.includes(roleId))
                  newValue = currentValue.concat(roleId);
                else newValue = currentValue.filter((v) => v !== roleId);
                onChange(newValue);
              };
              return (
                <View style={sharedStyles.optionsContainer}>
                  {matches(t('account.filter.default')) && (
                    <Pressable
                      key="roles-default"
                      onPress={() => onChange([])}
                      style={[
                        sharedStyles.optionChip,
                        isDefaultSelected && sharedStyles.optionChipSelected,
                      ]}
                    >
                      <Text
                        style={[
                          sharedStyles.optionChipText,
                          isDefaultSelected &&
                            sharedStyles.optionChipTextSelected,
                        ]}
                      >
                        {t('account.filter.default')}
                      </Text>
                    </Pressable>
                  )}
                  {accountRoles
                    .filter((role) => matches(role.name))
                    .map((role) => {
                      const isSelected = currentValue.includes(role.id);
                      return (
                        <Pressable
                          key={role.id}
                          onPress={() => onPressRole(role.id)}
                          style={[
                            sharedStyles.optionChip,
                            isSelected && sharedStyles.optionChipSelected,
                          ]}
                        >
                          <Text
                            style={[
                              sharedStyles.optionChipText,
                              isSelected && sharedStyles.optionChipTextSelected,
                            ]}
                          >
                            {role.name}
                          </Text>
                        </Pressable>
                      );
                    })}
                </View>
              );
            }}
          />
        </View>
      )}

      {/* LABELS (activity only) */}
      {showLabels && (
        <View style={sharedStyles.section}>
          <View style={sharedStyles.sectionHeader}>
            <Text style={[sharedStyles.sectionTitle, { marginBottom: 0 }]}>
              {t('activity.filter.labels')}
            </Text>
            {hasSpecificLabels && (
              <CombinedFilterToggle
                control={control}
                name="isCombinedLabelsFilter"
                accessibilityLabel={t('activity.filter.combinedGroups')}
                testID="labels-combined-filter-toggle"
              />
            )}
          </View>
          <LabelsFilterPills
            control={control}
            setValue={setValue}
            labels={labels}
            notLabeledLabel={t('activity.filter.notLabeled')}
            allLabel={t('activity.filter.all')}
            searchQuery={searchQuery}
          />
        </View>
      )}

      {/* DATE (activity only) */}
      {showDate && setValue && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('activity.filter.date')}
          </Text>
          <Controller
            control={control}
            name="dateType"
            render={({ field: { onChange, value } }) => (
              <View style={sharedStyles.optionsContainer}>
                {dateOptions
                  .filter((option) => matches(option.label))
                  .map((option) => {
                    const isSelected = value === option.value;
                    return (
                      <Pressable
                        key={option.value}
                        onPress={() => {
                          onChange(option.value);
                          if (option.value !== DateTypeFilter.CUSTOMDATE) {
                            setValue('startDate', '');
                            setValue('endDate', '');
                          }
                        }}
                        style={[
                          sharedStyles.optionChip,
                          isSelected && sharedStyles.optionChipSelected,
                        ]}
                      >
                        <Text
                          style={[
                            sharedStyles.optionChipText,
                            isSelected && sharedStyles.optionChipTextSelected,
                          ]}
                        >
                          {option.label}
                        </Text>
                      </Pressable>
                    );
                  })}
              </View>
            )}
          />
          {selectedDateType === DateTypeFilter.CUSTOMDATE && (
            <View style={sharedStyles.customDateContainer}>
              <DateRangeSelector
                control={control}
                setValue={setValue}
                styleContainer={sharedStyles.dateRangeContainer}
              />
            </View>
          )}
        </View>
      )}

      {/* SORT (activity only) */}
      {showSort && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('activity.filter.sort')}
          </Text>
          <Controller
            control={control}
            name="orderBy"
            render={({ field: { onChange, value } }) => (
              <View style={sharedStyles.optionsContainer}>
                {sortOptions
                  .filter((option) => matches(option.label))
                  .map((option) => {
                    const isSelected = value === option.value;
                    return (
                      <Pressable
                        key={option.value}
                        onPress={() => onChange(option.value)}
                        style={[
                          sharedStyles.optionChip,
                          isSelected && sharedStyles.optionChipSelected,
                        ]}
                      >
                        <Text
                          style={[
                            sharedStyles.optionChipText,
                            isSelected && sharedStyles.optionChipTextSelected,
                          ]}
                        >
                          {option.label}
                        </Text>
                      </Pressable>
                    );
                  })}
              </View>
            )}
          />
        </View>
      )}

      {/* COLUMNS (asset only) */}
      {showAssetColumns && (
        <View style={sharedStyles.section}>
          <Text style={sharedStyles.sectionTitle}>
            {t('assets.filter.columnSelection')}
          </Text>
          <Controller
            control={control}
            name="columns"
            render={({ field: { onChange, value } }) => {
              const currentValue = (value || []).filter(
                (c): c is string => c != null
              );
              const selectableBaseColumns = baseColumns.filter(
                (col) => col !== 'name'
              );
              const allColumns = Array.from(
                new Set([
                  ...selectableBaseColumns,
                  ...(isGlobalAdmin ? GlobalAdminColumns : []),
                  ...(workspaceRoutines?.map((r) => `routine:${r.id}`) || []),
                ])
              );
              const onPressColumn = (column: string) => {
                if (column === 'name') return;
                let newValue: string[];
                if (currentValue.includes(column))
                  newValue = currentValue.filter((c) => c !== column);
                else newValue = [...currentValue, column];
                if (!newValue.includes('name'))
                  newValue = ['name', ...newValue];
                onChange(newValue);
              };
              const effectiveValue = currentValue.includes('name')
                ? currentValue
                : ['name', ...currentValue];
              return (
                <View style={sharedStyles.optionsContainer}>
                  {matches(t('assets.filter.columns.name')) && (
                    <Pressable
                      key="name"
                      disabled={true}
                      style={[
                        sharedStyles.optionChip,
                        sharedStyles.optionChipSelected,
                        sharedStyles.optionChipDisabled,
                      ]}
                    >
                      <Text
                        style={[
                          sharedStyles.optionChipText,
                          sharedStyles.optionChipTextSelected,
                        ]}
                      >
                        {t('assets.filter.columns.name')}
                      </Text>
                    </Pressable>
                  )}
                  {allColumns
                    .filter((column) => matches(getAssetColumnLabel(column)))
                    .map((column) => {
                      const isSelected = effectiveValue.includes(column);
                      const isRoutine = column.startsWith('routine:');
                      const label = getAssetColumnLabel(column);
                      return (
                        <Pressable
                          key={column}
                          onPress={() => onPressColumn(column)}
                          style={[
                            sharedStyles.optionChip,
                            isRoutine && sharedStyles.routineChip,
                            isSelected && sharedStyles.optionChipSelected,
                            isRoutine &&
                              isSelected &&
                              sharedStyles.routineChipSelected,
                          ]}
                        >
                          <Text
                            style={[
                              sharedStyles.optionChipText,
                              isRoutine && sharedStyles.routineChipText,
                              isSelected && sharedStyles.optionChipTextSelected,
                            ]}
                          >
                            {label}
                          </Text>
                        </Pressable>
                      );
                    })}
                </View>
              );
            }}
          />
        </View>
      )}

      {/* EMPTY STATE — search active but nothing matches */}
      {query !== '' && !anyVisible && (
        <View style={sharedStyles.emptyState}>
          <Text style={sharedStyles.emptyStateText}>
            {t('common.filterSearch.noResults', {
              query: (searchQuery ?? '').trim(),
            })}
          </Text>
        </View>
      )}
    </>
  );
}

// ── Combined-filter toggle sub-component ──────────────────────────────────────

type CombinedFilterToggleProps = {
  control: Control<any>;
  name: 'isCombinedGroupsFilter' | 'isCombinedLabelsFilter';
  accessibilityLabel: string;
  testID: string;
};

const CombinedFilterToggle = ({
  control,
  name,
  accessibilityLabel,
  testID,
}: CombinedFilterToggleProps) => (
  <Controller
    control={control}
    name={name}
    render={({ field: { value, onChange } }) => (
      <Pressable
        onPress={() => onChange(!value)}
        style={[
          sharedStyles.combinedFilterToggle,
          !!value && sharedStyles.combinedFilterToggleActive,
        ]}
        accessibilityRole="checkbox"
        accessibilityState={{ checked: !!value }}
        accessibilityLabel={accessibilityLabel}
        testID={testID}
      >
        <Icon
          name="Link"
          size={16}
          color={value ? Colors.role.textOnInverse : Colors.role.textPrimary}
        />
      </Pressable>
    )}
  />
);

// ── Groups pills sub-component ────────────────────────────────────────────────

type GroupsFilterPillsProps = {
  control: Control<any>;
  setValue: UseFormSetValue<any> | undefined;
  fieldName: string;
  groups: any[];
  allLabel: string;
  notGroupedLabel: string;
  defaultLabel: string;
  /**
   * When true (activity/asset) render the All / Not-Grouped meta pills. When
   * false (account, which has no backend support for them) render a single
   * Default pill + group pills.
   */
  metaPills?: boolean;
  showUngrouped?: boolean;
  searchQuery?: string;
};

// View-only search match: empty/whitespace query matches everything.
const matchesQuery = (label: string, searchQuery?: string) => {
  const q = (searchQuery ?? '').trim().toLowerCase();
  return q === '' || label.toLowerCase().includes(q);
};

/**
 * The All / None meta-pill selector shared by the Groups and Labels sections —
 * one implementation so the two can't drift. Behavior:
 *  - Default = both meta pills selected (show everything).
 *  - Selecting a specific item is its own mode: it clears the meta pills, and
 *    selecting a meta pill clears specific items.
 *  - At least one pill is always selected — deselecting the last meta pill is a
 *    no-op (no silent flip to the other); deselecting the last specific item
 *    resets to the default.
 */
function MetaFilterPills({
  control,
  setValue,
  itemsField,
  includeAllField,
  includeNoneField,
  items,
  allLabel,
  noneLabel,
  showNone = true,
  searchQuery,
}: {
  control: Control<any>;
  setValue: UseFormSetValue<any> | undefined;
  itemsField: string;
  includeAllField: string;
  includeNoneField: string;
  items: { id: string; name: string }[];
  allLabel: string;
  noneLabel: string;
  showNone?: boolean;
  searchQuery?: string;
}) {
  const includeAll: boolean =
    useWatch({ control, name: includeAllField }) ?? true;
  const includeNone: boolean =
    useWatch({ control, name: includeNoneField }) ?? true;

  return (
    <Controller
      control={control}
      name={itemsField}
      render={({ field: { onChange, value } }) => {
        const selected = (value || []).filter(
          (v: unknown): v is string => v != null && v !== ''
        );

        // The pill transitions live in shared-logic (metaPillState) so this
        // component, the filter models and the home dashboard can't drift. The
        // state is spread across three RHF fields here, so we read them into a
        // MetaPillState, apply the shared transition, and write back only the
        // fields that changed (an unchanged write would needlessly re-trigger
        // the panel's debounced auto-submit).
        const current: MetaPillState = {
          includeAll,
          includeNone,
          items: selected,
        };
        const applyNext = (next: MetaPillState) => {
          if (next.includeAll !== includeAll) {
            setValue?.(includeAllField, next.includeAll, { shouldDirty: true });
          }
          if (next.includeNone !== includeNone) {
            setValue?.(includeNoneField, next.includeNone, {
              shouldDirty: true,
            });
          }
          const itemsChanged =
            next.items.length !== selected.length ||
            next.items.some((v, i) => v !== selected[i]);
          if (itemsChanged) onChange(next.items);
        };

        return (
          <View style={sharedStyles.optionsContainer}>
            {matchesQuery(allLabel, searchQuery) && (
              <FilterChip
                label={allLabel}
                selected={includeAll}
                onPress={() => applyNext(toggleMetaPill(current, 'all'))}
              />
            )}
            {showNone && matchesQuery(noneLabel, searchQuery) && (
              <FilterChip
                label={noneLabel}
                selected={includeNone}
                onPress={() => applyNext(toggleMetaPill(current, 'none'))}
              />
            )}
            {items
              .filter((item) => matchesQuery(item.name, searchQuery))
              .map((item) => (
                <FilterChip
                  key={item.id}
                  label={item.name}
                  selected={selected.includes(item.id)}
                  onPress={() => applyNext(toggleMetaItem(current, item.id))}
                />
              ))}
          </View>
        );
      }}
    />
  );
}

/**
 * Simple "Default + items" selector used by the account variant, which has no
 * backend support for the All / Not-Grouped meta pills.
 */
function LegacyDefaultPills({
  control,
  fieldName,
  items,
  defaultLabel,
  searchQuery,
}: {
  control: Control<any>;
  fieldName: string;
  items: { id: string; name: string }[];
  defaultLabel: string;
  searchQuery?: string;
}) {
  return (
    <Controller
      control={control}
      name={fieldName}
      render={({ field: { onChange, value } }) => {
        const selected = (value || []).filter(
          (v: unknown): v is string => v != null && v !== ''
        );
        const onPressItem = (id: string) =>
          onChange(
            selected.includes(id)
              ? selected.filter((v: string) => v !== id)
              : selected.concat(id)
          );
        return (
          <View style={sharedStyles.optionsContainer}>
            {matchesQuery(defaultLabel, searchQuery) && (
              <FilterChip
                label={defaultLabel}
                selected={selected.length === 0}
                onPress={() => onChange([])}
              />
            )}
            {items
              .filter((item) => matchesQuery(item.name, searchQuery))
              .map((item) => (
                <FilterChip
                  key={item.id}
                  label={item.name}
                  selected={selected.includes(item.id)}
                  onPress={() => onPressItem(item.id)}
                />
              ))}
          </View>
        );
      }}
    />
  );
}

function GroupsFilterPills({
  control,
  setValue,
  fieldName,
  groups,
  allLabel,
  notGroupedLabel,
  defaultLabel,
  metaPills = true,
  showUngrouped = true,
  searchQuery,
}: GroupsFilterPillsProps) {
  // Account has no backend support for the meta pills — keep the simple
  // Default + groups selector for it.
  if (!metaPills) {
    return (
      <LegacyDefaultPills
        control={control}
        fieldName={fieldName}
        items={groups}
        defaultLabel={defaultLabel}
        searchQuery={searchQuery}
      />
    );
  }

  return (
    <MetaFilterPills
      control={control}
      setValue={setValue}
      itemsField={fieldName}
      includeAllField="includeAllGroups"
      includeNoneField="includeUngrouped"
      items={groups}
      allLabel={allLabel}
      noneLabel={notGroupedLabel}
      showNone={showUngrouped}
      searchQuery={searchQuery}
    />
  );
}

// ── Labels pills sub-component ────────────────────────────────────────────────

type LabelsFilterPillsProps = {
  control: Control<any>;
  setValue: UseFormSetValue<any> | undefined;
  labels: any[];
  allLabel: string;
  notLabeledLabel: string;
  searchQuery?: string;
};

function LabelsFilterPills({
  control,
  setValue,
  labels,
  allLabel,
  notLabeledLabel,
  searchQuery,
}: LabelsFilterPillsProps) {
  return (
    <MetaFilterPills
      control={control}
      setValue={setValue}
      itemsField="labelIds"
      includeAllField="includeAllLabels"
      includeNoneField="includeUnlabeled"
      items={labels}
      allLabel={allLabel}
      noneLabel={notLabeledLabel}
      searchQuery={searchQuery}
    />
  );
}

const sharedStyles = themedStyleSheet((role) => ({
  // Canonical chip + section styling is owned by filterChip.tsx; source it from
  // there so the inline controllers below can't drift from FilterChip.
  section: filterChipStyleValues.section,
  sectionTitle: filterChipStyleValues.sectionTitle,
  optionsContainer: filterChipStyleValues.optionsContainer,
  optionChip: filterChipStyleValues.chip,
  optionChipSelected: filterChipStyleValues.chipSelected,
  optionChipText: filterChipStyleValues.chipText,
  optionChipTextSelected: filterChipStyleValues.chipTextSelected,
  optionChipDisabled: filterChipStyleValues.chipDisabled,
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: tokens.spacing.sm,
    marginBottom: tokens.spacing.md,
  },
  combinedFilterToggle: {
    borderRadius: tokens.borderRadius.lg,
    paddingVertical: tokens.spacing.xxs,
    paddingHorizontal: tokens.spacing.xs,
    backgroundColor: role.surfaceHover,
  },
  combinedFilterToggleActive: {
    backgroundColor: role.surfaceInverse,
  },
  routineChip: {
    backgroundColor: role.surfaceInfoSoft,
  },
  routineChipSelected: {
    backgroundColor: tokens.colors.semantic.info,
    borderColor: tokens.colors.semantic.info,
  },
  routineChipText: {
    color: role.textInfo,
  },
  customDateContainer: { marginTop: tokens.spacing.lg },
  dateRangeContainer: { gap: tokens.spacing.md },
  emptyState: {
    paddingTop: tokens.spacing['2xl'],
    alignItems: 'center',
  },
  emptyStateText: {
    ...tokens.typography.body.medium,
    color: role.textSecondary,
    textAlign: 'center',
  },
}));
